import React, { useState, useEffect, useCallback, useRef, type FormEvent } from 'react';
import { useBrand } from '../../../app/config/brand';
import { useApi, API_BASE_URL } from '../../../api/useApi';
import { Card } from '../../../shared/components/ui/Card';
import { Icons } from '../../../shared/icons/Icons';
import type { DeadlineItem, DeadlineType } from '@lds-efsp/shared-contracts';

// ── Deadline type labels ──

const TYPE_LABELS: Record<DeadlineType, string> = {
  hearing: 'Hearing',
  response: 'Response due',
  discovery: 'Discovery',
  statute: 'Statute of limitations',
  trial: 'Trial',
  custom: 'Custom',
};

// ── Helpers ──

function formatMonth(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleString('en-US', { month: 'short' }).toUpperCase();
}

function formatDay(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return String(d.getDate());
}

function urgencyClass(daysRemaining: number): 'urgent' | 'soon' | 'normal' | 'overdue' {
  if (daysRemaining < 0) return 'overdue';
  if (daysRemaining <= 7) return 'urgent';
  if (daysRemaining <= 14) return 'soon';
  return 'normal';
}

function daysLabel(daysRemaining: number): string {
  if (daysRemaining < 0) return `${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) !== 1 ? 's' : ''} overdue`;
  if (daysRemaining === 0) return 'Today';
  if (daysRemaining === 1) return 'Tomorrow';
  return `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}`;
}

// ── Add/Edit Modal ──

interface DeadlineFormData {
  title: string;
  dueDate: string;
  deadlineType: DeadlineType;
  caseNumber: string;
  caseTitle: string;
  notes: string;
}

const EMPTY_FORM: DeadlineFormData = {
  title: '',
  dueDate: '',
  deadlineType: 'custom',
  caseNumber: '',
  caseTitle: '',
  notes: '',
};

interface DeadlineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: DeadlineFormData) => void;
  initial?: Partial<DeadlineFormData>;
  isLoading: boolean;
  isEdit: boolean;
}

const DeadlineModal: React.FC<DeadlineModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initial,
  isLoading,
  isEdit,
}) => {
  const brand = useBrand();
  const [form, setForm] = useState<DeadlineFormData>({ ...EMPTY_FORM, ...initial });

  /* eslint-disable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- reset form on open */
  useEffect(() => {
    if (isOpen) setForm({ ...EMPTY_FORM, ...initial });
  }, [isOpen, initial]);
  /* eslint-enable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */

  if (!isOpen) return null;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  const set = (field: keyof DeadlineFormData, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const inputStyle = {
    borderColor: brand.colors.border,
    color: brand.colors.text,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deadline-modal-title"
      >
        <div className="px-6 py-4 border-b" style={{ borderColor: brand.colors.border }}>
          <div className="flex items-center justify-between">
            <h2
              id="deadline-modal-title"
              className="text-lg font-semibold"
              style={{ color: brand.colors.secondary }}
            >
              {isEdit ? 'Edit Deadline' : 'Add Deadline'}
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <Icons.X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>
              Title *
            </label>
            <input
              type="text"
              required
              maxLength={255}
              value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder="e.g., Court response due"
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={inputStyle}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>
                Due date *
              </label>
              <input
                type="date"
                required
                value={form.dueDate}
                onChange={e => set('dueDate', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border text-sm"
                style={inputStyle}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>
                Type
              </label>
              <select
                value={form.deadlineType}
                onChange={e => set('deadlineType', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border text-sm"
                style={inputStyle}
              >
                {Object.entries(TYPE_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>
                Case number
              </label>
              <input
                type="text"
                maxLength={100}
                value={form.caseNumber}
                onChange={e => set('caseNumber', e.target.value)}
                placeholder="e.g., CIVRS2506414"
                className="w-full px-3 py-2 rounded-lg border text-sm"
                style={inputStyle}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>
                Case title
              </label>
              <input
                type="text"
                maxLength={500}
                value={form.caseTitle}
                onChange={e => set('caseTitle', e.target.value)}
                placeholder="e.g., Smith v. Jones"
                className="w-full px-3 py-2 rounded-lg border text-sm"
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>
              Notes
            </label>
            <textarea
              maxLength={2000}
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="Optional notes..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
              style={inputStyle}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors hover:bg-gray-50"
              style={{ borderColor: brand.colors.border, color: brand.colors.text }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !form.title || !form.dueDate}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
            >
              {isLoading ? 'Saving...' : isEdit ? 'Update' : 'Add Deadline'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main Widget ──

interface DeadlineWidgetProps {
  /** Reports the widget's current deadline list whenever it changes (fetch,
   *  add, edit, delete). Lets the dashboard derive its Upcoming Deadlines stat
   *  from the SAME records this panel renders, so the two counts can never
   *  disagree (Walter R30). */
  onDeadlinesChange?: (deadlines: DeadlineItem[]) => void;
}

const DeadlineWidget: React.FC<DeadlineWidgetProps> = ({ onDeadlinesChange }) => {
  const brand = useBrand();
  const { get, post, patch, del } = useApi();
  const [deadlines, setDeadlines] = useState<DeadlineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDeadline, setEditingDeadline] = useState<DeadlineItem | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchDeadlines = useCallback(async () => {
    try {
      const res = await get<{ data: DeadlineItem[] }>(`${API_BASE_URL}/api/deadlines?days=90&includeOverdue=true`);
      setDeadlines(res.data);
    } catch {
      // silently degrade — widget shows empty state
    } finally {
      setLoading(false);
    }
  }, [get]);

  // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
  useEffect(() => { fetchDeadlines(); }, [fetchDeadlines]);

  // Mirror every list change (fetch + CRUD) to the parent so derived counts
  // stay in lockstep with what this panel shows (R30). Latest-ref pattern:
  // the ref is synced in its own effect, never during render (the
  // react-compiler lint flags render-time ref access).
  const onDeadlinesChangeRef = useRef(onDeadlinesChange);
  useEffect(() => { onDeadlinesChangeRef.current = onDeadlinesChange; });
  useEffect(() => { onDeadlinesChangeRef.current?.(deadlines); }, [deadlines]);

  const handleAdd = () => {
    setEditingDeadline(null);
    setModalOpen(true);
  };

  const handleEdit = (d: DeadlineItem) => {
    setEditingDeadline(d);
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await del(`${API_BASE_URL}/api/deadlines/${id}`);
      setDeadlines(prev => prev.filter(d => d.id !== id));
    } catch {
      // TODO: toast error
    }
  };

  const handleSubmit = async (form: DeadlineFormData) => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        dueDate: form.dueDate,
        deadlineType: form.deadlineType as DeadlineType,
        caseNumber: form.caseNumber || undefined,
        caseTitle: form.caseTitle || undefined,
        notes: form.notes || undefined,
      };

      if (editingDeadline) {
        await patch(`${API_BASE_URL}/api/deadlines/${editingDeadline.id}`, payload);
      } else {
        await post(`${API_BASE_URL}/api/deadlines`, payload);
      }

      setModalOpen(false);
      await fetchDeadlines();
    } catch {
      // TODO: toast error
    } finally {
      setSaving(false);
    }
  };

  const urgencyColor = (days: number) => {
    const u = urgencyClass(days);
    if (u === 'overdue' || u === 'urgent') return brand.colors.error;
    if (u === 'soon') return brand.colors.warning;
    return brand.colors.textMuted;
  };

  const urgencyBg = (days: number) => {
    const u = urgencyClass(days);
    if (u === 'overdue' || u === 'urgent') return '#FEE2E2';
    if (u === 'soon') return '#FEF3C7';
    return brand.colors.bgLight;
  };

  return (
    <>
      <Card padding={false}>
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold" style={{ color: brand.colors.secondary }}>
            Upcoming deadlines
          </h2>
          {deadlines.length > 0 && (
            <span className="text-xs" style={{ color: brand.colors.textMuted }}>
              {deadlines.length} total
            </span>
          )}
        </div>

        <div className="px-5 pb-4">
          {loading ? (
            <div className="py-8 text-center text-sm" style={{ color: brand.colors.textLight }}>
              Loading...
            </div>
          ) : deadlines.length === 0 ? (
            <div className="py-8 text-center">
              <Icons.Clock className="w-8 h-8 mx-auto mb-2" style={{ color: brand.colors.textMuted }} />
              <p className="text-sm" style={{ color: brand.colors.textLight }}>
                No upcoming deadlines
              </p>
              <p className="text-xs mt-1" style={{ color: brand.colors.textMuted }}>
                Add deadlines to track court dates and filing due dates
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {deadlines.map(d => (
                <div
                  key={d.id}
                  className="flex gap-3 group cursor-pointer rounded-lg p-2 -mx-2 transition-colors hover:bg-gray-50"
                  onClick={() => handleEdit(d)}
                >
                  {/* Date block */}
                  <div
                    className="flex-shrink-0 w-12 h-12 rounded-lg flex flex-col items-center justify-center"
                    style={{
                      backgroundColor: urgencyClass(d.daysRemaining) === 'urgent' || urgencyClass(d.daysRemaining) === 'overdue'
                        ? '#FEE2E2'
                        : brand.colors.bgLight,
                      border: `1px solid ${urgencyClass(d.daysRemaining) === 'urgent' || urgencyClass(d.daysRemaining) === 'overdue'
                        ? '#FECACA'
                        : brand.colors.border}`,
                    }}
                  >
                    <span
                      className="text-[10px] font-bold leading-none"
                      style={{ color: urgencyColor(d.daysRemaining) }}
                    >
                      {formatMonth(d.dueDate)}
                    </span>
                    <span
                      className="text-lg font-semibold leading-tight"
                      style={{ color: brand.colors.secondary }}
                    >
                      {formatDay(d.dueDate)}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" style={{ color: brand.colors.text }}>
                      {d.title}
                    </div>
                    {d.caseTitle && (
                      <div className="text-xs truncate mt-0.5" style={{ color: brand.colors.link }}>
                        {d.caseTitle}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className="text-[11px] font-medium px-1.5 py-0.5 rounded"
                        style={{
                          backgroundColor: urgencyBg(d.daysRemaining),
                          color: urgencyColor(d.daysRemaining),
                        }}
                      >
                        {daysLabel(d.daysRemaining)}
                      </span>
                      <span className="text-[11px]" style={{ color: brand.colors.textMuted }}>
                        {TYPE_LABELS[d.deadlineType]}
                      </span>
                    </div>
                  </div>

                  {/* Delete button (visible on hover) */}
                  <button
                    className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50"
                    onClick={(e) => { e.stopPropagation(); handleDelete(d.id); }}
                    aria-label={`Delete deadline: ${d.title}`}
                  >
                    <Icons.X className="w-3.5 h-3.5" style={{ color: brand.colors.error }} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={handleAdd}
            className="w-full mt-3 py-2 rounded-lg text-sm font-medium border border-dashed transition-colors hover:bg-gray-50"
            style={{ borderColor: brand.colors.border, color: brand.colors.link }}
          >
            <Icons.Plus className="w-4 h-4 inline-block -mt-0.5 mr-1" />
            Add a deadline
          </button>
        </div>
      </Card>

      <DeadlineModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        isLoading={saving}
        isEdit={!!editingDeadline}
        initial={editingDeadline ? {
          title: editingDeadline.title,
          dueDate: editingDeadline.dueDate,
          deadlineType: editingDeadline.deadlineType,
          caseNumber: editingDeadline.caseNumber ?? '',
          caseTitle: editingDeadline.caseTitle ?? '',
          notes: editingDeadline.notes ?? '',
        } : undefined}
      />
    </>
  );
};

export default DeadlineWidget;
